/**
 * SUPABASE CLIENT CONFIGURATION
 * Enhanced with helper functions for API layer
 */
import { createClient } from '@supabase/supabase-js';

// Environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate URL format
const isValidUrl = (url) => {
    try {
        const parsed = new URL(url);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
        return false;
    }
};

const hasValidConfig = supabaseUrl && supabaseAnonKey && isValidUrl(supabaseUrl);

if (!hasValidConfig) {
    console.warn('⚠️ Supabase not configured or invalid URL. Auth features will use local fallback.');
    console.warn('   Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file');
}

/** @type {import('@supabase/supabase-js').SupabaseClient | null} */
export const supabase = hasValidConfig
    ? createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
            autoRefreshToken: true,
            persistSession: true,
            detectSessionInUrl: true,
        },
        realtime: {
            params: {
                eventsPerSecond: 10,
            },
        },
        db: {
            schema: 'public',
        },
    })
    : null;

// ============================================================================
// HELPER FUNCTIONS FOR API LAYER
// ============================================================================

/**
 * Get current authenticated user
 * @returns {Promise<import('@supabase/supabase-js').User>}
 * @throws {Error} If not authenticated or Supabase not configured
 */
export async function getCurrentUser() {
    if (!supabase) {
        throw new Error('Supabase not configured');
    }

    const { data: { user }, error } = await supabase.auth.getUser();

    if (error) {
        throw new Error(`Auth error: ${error.message}`);
    }
    if (!user) {
        throw new Error('Not authenticated');
    }

    return user;
}

/**
 * Get user ID safely
 * @returns {Promise<string>}
 * @throws {Error} If not authenticated
 */
export async function getUserId() {
    const user = await getCurrentUser();
    return user.id;
}

/**
 * Check if user has active subscription
 * @returns {Promise<boolean>}
 */
export async function hasActiveSubscription() {
    if (!supabase) return false;

    try {
        const userId = await getUserId();

        const { data, error } = await supabase
            .from('subscriptions')
            .select('status, end_date')
            .eq('user_id', userId)
            .single();

        if (error || !data) return false;

        if (data.status === 'active') {
            if (!data.end_date) return true; // Lifetime subscription
            return new Date(data.end_date) > new Date();
        }

        return false;
    } catch (error) {
        console.error('hasActiveSubscription error:', error);
        return false;
    }
}

/**
 * Get subscription tier
 * @returns {Promise<'STARTER' | 'PRO' | 'PRO'>}
 */
export async function getSubscriptionTier() {
    if (!supabase) return 'STARTER';

    try {
        const userId = await getUserId();

        const { data, error } = await supabase
            .from('subscriptions')
            .select('tier, status, end_date')
            .eq('user_id', userId)
            .single();

        if (error || !data) return 'STARTER';

        // Check if subscription is active
        if (data.status !== 'active') return 'STARTER';
        if (data.end_date && new Date(data.end_date) < new Date()) return 'STARTER';

        return data.tier || 'STARTER';
    } catch (error) {
        console.error('getSubscriptionTier error:', error);
        return 'STARTER';
    }
}

/**
 * Create API error object
 * @param {string} message 
 * @param {unknown} error 
 * @returns {{ message: string, code?: string, details?: unknown, timestamp: string }}
 */
export function createApiError(message, error) {
    return {
        message,
        code: error?.code || undefined,
        details: error,
        timestamp: new Date().toISOString(),
    };
}

export default supabase;
